import React from 'react';

/* ─────────────────────────────────────────────────────────────────
   "FERNWEG" — concept: boutique travel-goods e-commerce storefront
   A fixed, self-contained visual identity — does not follow the
   site's light/dark theme, by design.

   Pass `full` to render the complete, scrollable storefront (used
   inside the concept modal); omit it for the small card preview.
───────────────────────────────────────────────────────────────── */

const PRODUCTS = [
  { name: 'Weekender Duffel', price: '₹6,400', swatch: '#7C5A3A' },
  { name: 'Passport Wallet', price: '₹1,850', swatch: '#5A6B52' },
  { name: 'Canvas Tote', price: '₹2,300', swatch: '#8B6F4E' },
];

const MORE_PRODUCTS = [
  { name: 'Rolltop Backpack', price: '₹5,100', swatch: '#4A5A44' },
  { name: 'Leather Luggage Tag', price: '₹650', swatch: '#9C7A4E' },
  { name: 'Field Notebook', price: '₹950', swatch: '#6B4E3A' },
];

const CATEGORIES = ['Duffels', 'Backpacks', 'Small Leather Goods', 'Travel Accessories'];

const TESTIMONIALS = [
  { quote: 'Three years of weekly flights and the stitching still holds. Worth every rupee.', name: 'Meera K.' },
  { quote: 'The kind of bag you stop noticing you\'re carrying.', name: 'Arjun D.' },
];

const FernwegStore = ({ full = false }) => (
  <div className={`frw-frame ${full ? 'frw-frame-full' : ''}`}>
    <div className="frw-titlebar">
      <span className="frw-dot" style={{ background: '#EA5F5F' }} />
      <span className="frw-dot" style={{ background: '#E8B34C' }} />
      <span className="frw-dot" style={{ background: '#4CAF7D' }} />
    </div>

    <div className="frw-body">
      <nav className="frw-nav">
        <span className="frw-wordmark">Fernweg</span>
        <div className="frw-nav-links">
          <span>Journeys</span><span>Goods</span><span>Journal</span>
        </div>
        <span className="frw-cart">Bag (2)</span>
      </nav>

      <div className="frw-hero">
        <div className="frw-hero-swatch" />
        <div className="frw-hero-copy">
          <p className="frw-eyebrow">NEW ARRIVAL</p>
          <h3 className="frw-headline">Made for the long way round.</h3>
          <p className="frw-sub">Full-grain leather goods, built to age well on every route you take.</p>
          <button className="frw-cta">Shop the Edit</button>
        </div>
      </div>

      {full && (
        <div className="frw-categories">
          {CATEGORIES.map(c => <span key={c} className="frw-category-pill">{c}</span>)}
        </div>
      )}

      <div className="frw-grid">
        {PRODUCTS.map(p => (
          <div key={p.name} className="frw-product">
            <div className="frw-product-swatch" style={{ background: p.swatch }} />
            <p className="frw-product-name">{p.name}</p>
            <p className="frw-product-price">{p.price}</p>
          </div>
        ))}
        {full && MORE_PRODUCTS.map(p => (
          <div key={p.name} className="frw-product">
            <div className="frw-product-swatch" style={{ background: p.swatch }} />
            <p className="frw-product-name">{p.name}</p>
            <p className="frw-product-price">{p.price}</p>
          </div>
        ))}
      </div>

      {full && (
        <>
          <div className="frw-testimonials">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="frw-testimonial">
                <p className="frw-quote">&ldquo;{t.quote}&rdquo;</p>
                <p className="frw-quote-name">— {t.name}</p>
              </div>
            ))}
          </div>

          <footer className="frw-footer">
            <div>
              <span className="frw-wordmark">Fernweg</span>
              <p className="frw-footer-tag">Travel goods built to be used, not admired.</p>
            </div>
            <div className="frw-footer-cols">
              <div>
                <p className="frw-footer-head">Shop</p>
                <p>Duffels</p><p>Backpacks</p><p>Accessories</p>
              </div>
              <div>
                <p className="frw-footer-head">Studio</p>
                <p>Our Story</p><p>Journal</p><p>Contact</p>
              </div>
            </div>
          </footer>
        </>
      )}
    </div>

    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,500;0,600;1,500&family=Work+Sans:wght@400;500;600&display=swap');

      .frw-frame {
        --bg: #1B2B22; --panel: #223428; --stone: #C9C2B3;
        --text: #F2EFE7; --muted: #9CA895; --accent: #B5613A;
        background: var(--bg); border: 1px solid var(--panel); border-radius: 14px;
        overflow: hidden; font-family: 'Work Sans', sans-serif; box-shadow: 0 20px 50px rgba(0,0,0,0.35);
        width: 100%; height: 100%; display: flex; flex-direction: column;
      }
      .frw-frame-full { height: auto; min-height: 100%; border-radius: 0; box-shadow: none; }
      .frw-titlebar { display: flex; gap: 6px; padding: 12px 16px; background: var(--panel); }
      .frw-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }

      .frw-body { padding: 20px 22px; flex: 1; display: flex; flex-direction: column; gap: 18px; min-height: 0; }
      .frw-frame-full .frw-body { padding: 26px 30px 50px; }

      .frw-nav { display: flex; align-items: center; justify-content: space-between; }
      .frw-wordmark { font-family: 'Fraunces', serif; font-weight: 600; font-size: 17px; color: var(--text); }
      .frw-nav-links { display: flex; gap: 14px; font-size: 11px; color: var(--muted); }
      .frw-cart { font-size: 11px; color: var(--stone); border: 1px solid var(--panel); padding: 5px 10px; border-radius: 999px; }

      .frw-hero { display: grid; grid-template-columns: 0.9fr 1.1fr; gap: 18px; align-items: center; background: var(--panel); border-radius: 12px; padding: 16px; }
      .frw-hero-swatch { height: 110px; border-radius: 8px; background: #5A6B52; }
      .frw-eyebrow { font-size: 10px; font-weight: 600; letter-spacing: 0.14em; color: var(--accent); margin: 0 0 6px; }
      .frw-headline { font-family: 'Fraunces', serif; font-style: italic; font-weight: 500; font-size: 18px; color: var(--text); margin: 0 0 8px; line-height: 1.25; text-wrap: balance; }
      .frw-sub { font-size: 11.5px; color: var(--muted); line-height: 1.55; margin: 0 0 12px; }
      .frw-cta { background: var(--accent); color: #fff; border: none; padding: 8px 16px; border-radius: 7px; font-size: 12px; font-weight: 600; font-family: 'Work Sans', sans-serif; cursor: default; }

      .frw-categories { display: flex; flex-wrap: wrap; gap: 8px; }
      .frw-category-pill { font-size: 11px; color: var(--stone); border: 1px solid var(--panel); background: var(--panel); padding: 6px 13px; border-radius: 999px; }

      .frw-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
      .frw-product-swatch { height: 64px; border-radius: 8px; margin-bottom: 8px; }
      .frw-product-name { font-size: 11.5px; color: var(--text); margin: 0 0 3px; font-weight: 500; }
      .frw-product-price { font-size: 11px; color: var(--muted); margin: 0; }

      .frw-testimonials { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding-top: 6px; }
      .frw-testimonial { background: var(--panel); border-radius: 10px; padding: 16px; }
      .frw-quote { font-family: 'Fraunces', serif; font-style: italic; font-size: 13px; color: var(--text); line-height: 1.5; margin: 0 0 10px; text-wrap: balance; }
      .frw-quote-name { font-size: 11px; color: var(--muted); margin: 0; }

      .frw-footer { display: flex; justify-content: space-between; gap: 24px; border-top: 1px solid var(--panel); padding-top: 20px; margin-top: 6px; }
      .frw-footer-tag { font-size: 11px; color: var(--muted); margin: 8px 0 0; max-width: 220px; line-height: 1.5; }
      .frw-footer-cols { display: flex; gap: 36px; font-size: 11px; color: var(--muted); line-height: 2; }
      .frw-footer-head { color: var(--stone); font-weight: 600; margin: 0 0 2px; }
    `}</style>
  </div>
);

export default FernwegStore;
